import { useState } from 'react'
import { Download } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { ViewOnlyBadge } from '@/components/templates/app-shell'

type ChapterOption = { id: number | null; name: string }
type MemberRow = {
  id: number
  contact_name: string
  member_number: string | null
  level: string
  status: string
  chapter_names: string[]
}
type ChapterRow = { id: string; chapter_name: string; archived: boolean; membership_count: number }
type Report = {
  rows: MemberRow[] | ChapterRow[]
  grouped: boolean
  grouped_total_warning: string | null
  filter_options: ChapterOption[]
}

export function MembershipReportPage() {
  const [chapterId, setChapterId] = useState<string>('all')
  const [grouped, setGrouped] = useState(false)
  const [selection, setSelection] = useState({ chapterId: 'all', grouped: false })
  const query = useQuery({
    queryKey: ['membership-report', selection.chapterId, selection.grouped],
    queryFn: () => getJSON<{ membership_report: Report }>(reportPath(selection)),
    retry: false,
  })
  const report = query.data?.membership_report
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold">Membership report</h1>
          <ViewOnlyBadge />
        </div>
        <p className="text-caption text-muted-foreground">Active memberships and their chapters</p>
      </header>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="membership-report-chapter">Chapter</Label>
            <Select value={chapterId} onValueChange={setChapterId}>
              <SelectTrigger id="membership-report-chapter" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All chapters</SelectItem>
                {(report?.filter_options ?? [])
                  .filter((option) => option.id !== null)
                  .map((option) => (
                    <SelectItem
                      key={option.id ?? 'at-large'}
                      value={option.id?.toString() ?? 'at-large'}
                    >
                      {option.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" onClick={() => setSelection({ chapterId, grouped })}>
            Apply
          </Button>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="membership-report-grouping">Group by</Label>
            <Select
              value={grouped ? 'chapter' : 'member'}
              onValueChange={(value) => setGrouped(value === 'chapter')}
            >
              <SelectTrigger id="membership-report-grouping" className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="chapter">Chapter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button size="sm" variant="outline" asChild>
          <a href={exportPath(selection)} download>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </a>
        </Button>
      </div>
      {report?.grouped_total_warning && (
        <p className="mt-3 text-caption text-muted-foreground">{report.grouped_total_warning}</p>
      )}
      <div className="mt-4">
        <ReportBody report={report} loading={query.isLoading} />
      </div>
    </div>
  )
}

function reportPath(selection: { chapterId: string; grouped: boolean }) {
  const params = new URLSearchParams()
  if (selection.chapterId !== 'all')
    params.set('chapter_id', selection.chapterId === 'at-large' ? '' : selection.chapterId)
  if (selection.grouped) params.set('group_by', 'chapter')
  const query = params.toString()
  return `/api/v1/membership_report${query ? `?${query}` : ''}`
}

function exportPath(selection: { chapterId: string; grouped: boolean }) {
  return reportPath(selection).replace('/membership_report', '/membership_report/export')
}

function ReportBody({ report, loading }: { report?: Report; loading: boolean }) {
  if (loading)
    return (
      <p role="status" className="text-body-sm text-muted-foreground">
        Loading membership report…
      </p>
    )
  if (!report || report.rows.length === 0)
    return <EmptyState title="No memberships found" description="Try a different chapter filter." />
  return report.grouped ? (
    <GroupedRows rows={report.rows as ChapterRow[]} />
  ) : (
    <MemberRows rows={report.rows as MemberRow[]} />
  )
}

function MemberRows({ rows }: { rows: MemberRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Member</TableHead>
          <TableHead>Member number</TableHead>
          <TableHead>Membership level</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Chapters</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{row.contact_name}</TableCell>
            <TableCell>{row.member_number ?? '—'}</TableCell>
            <TableCell>{row.level}</TableCell>
            <TableCell>{row.status}</TableCell>
            <TableCell>{row.chapter_names.join(', ') || 'At large'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function GroupedRows({ rows }: { rows: ChapterRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Chapter</TableHead>
          <TableHead>Memberships</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              {row.chapter_name}
              {row.archived ? ' (Archived)' : ''}
            </TableCell>
            <TableCell>{row.membership_count}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
